import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import {
  Mail,
  Lock,
  AlertCircle,
  ArrowRight,
  ShieldCheck,
  FileSignature,
  Activity,
  CheckCircle2,
  Sparkles,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

// iter165f — Sign-in page rebuilt from the ground up. The previous layout
// (auth-layout / auth-left / auth-right CSS) was replaced with a fully
// Tailwind-driven split-screen that matches the navy ClinicalFlow brand
// (same palette as the landing CardSwap + MagicBento). The right pane now
// shows live "audit-trail" style activity, three branded KPI tiles, and a
// rotating tagline. data-testids are preserved so existing tests still
// pass: login-page, login-home-link, email-input, password-input,
// login-btn, register-link, forgot-password-link.

const heroSlides = [
  {
    title: 'Clinical research, fully orchestrated.',
    subtitle:
      'From feasibility to signed contract — manage every stage of your research agreements with workflows configured for your institution.',
  },
  {
    title: 'Your workflows. Your governance.',
    subtitle:
      'Multi-stage Director / Executive Director / Chairman review, per-document e-signatures, and a tamper-proof audit trail.',
  },
  {
    title: 'One platform. Every research agreement.',
    subtitle:
      'Feasibility studies, CTAs, financial transactions and IRB submissions — running on a single auditable backbone.',
  },
];

const activityFeed = [
  {
    icon: FileSignature,
    accent: 'text-cyan-300',
    ring: 'ring-cyan-400/30',
    bg: 'bg-cyan-400/10',
    text: 'Chairman signed',
    meta: 'CTA-2026-000017 · 2m ago',
  },
  {
    icon: CheckCircle2,
    accent: 'text-emerald-300',
    ring: 'ring-emerald-400/30',
    bg: 'bg-emerald-400/10',
    text: 'Feasibility approved',
    meta: 'FEA-2026-000042 · 14m ago',
  },
  {
    icon: ShieldCheck,
    accent: 'text-amber-300',
    ring: 'ring-amber-400/30',
    bg: 'bg-amber-400/10',
    text: 'IRB review submitted',
    meta: 'IRB-2026-000009 · 41m ago',
  },
  {
    icon: Activity,
    accent: 'text-violet-300',
    ring: 'ring-violet-400/30',
    bg: 'bg-violet-400/10',
    text: 'New site engaged',
    meta: 'PharmaCorp · 1h ago',
  },
];

const kpis = [
  { value: '200+', label: 'Active users', accent: 'from-cyan-300 to-sky-400' },
  { value: '30+', label: 'Specialties', accent: 'from-violet-300 to-fuchsia-400' },
  { value: '15+', label: 'Sponsors', accent: 'from-amber-300 to-orange-400' },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [heroIndex, setHeroIndex] = useState(0);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Surface errors passed from AuthCallback / redirect chains.
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const hero = heroSlides[heroIndex];

  return (
    <div
      className="min-h-screen w-full grid lg:grid-cols-12 bg-slate-50"
      data-testid="login-page"
    >
      {/* ====================== LEFT — Form ====================== */}
      <div className="relative lg:col-span-5 flex items-center justify-center px-6 py-10 lg:py-16 overflow-hidden">
        {/* Soft gradient blobs to add depth to an otherwise flat panel */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 w-[420px] h-[420px] bg-blue-100/60 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[380px] h-[380px] bg-indigo-100/50 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-md">
          {/* Back-to-home + Brand mark */}
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-10"
            data-testid="login-home-link"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to home
          </Link>

          <div className="flex items-center gap-3 mb-10">
            <img
              src="/favicon-192.png"
              alt="ClinicalFlow logo"
              className="h-12 w-12 object-contain drop-shadow-sm"
            />
            <div className="leading-none">
              <div className="text-2xl font-bold tracking-tight text-slate-900">
                ClinicalFlow
              </div>
              <div className="mt-1.5 text-[11px] uppercase tracking-[0.22em] font-mono text-slate-500">
                Research Request Portal
              </div>
            </div>
          </div>

          {/* Form card — glassy with subtle ring + shadow */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-blue-200/40 via-indigo-200/30 to-cyan-200/40 rounded-3xl blur-xl pointer-events-none" />
            <div className="relative rounded-2xl bg-white/90 backdrop-blur-xl ring-1 ring-slate-200/80 shadow-2xl shadow-slate-300/40 p-7 sm:p-8">
              <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                  Welcome back
                </h1>
                <p className="mt-1.5 text-sm text-slate-500">
                  Sign in to your ClinicalFlow account
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div
                    className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
                    data-testid="login-error"
                  >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span className="leading-relaxed">{error}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Email address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@hospital.org"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-[#1e3a5f]/30 focus-visible:border-[#1e3a5f]"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Password
                    </Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs font-medium text-[#1e3a5f] hover:text-[#152c4a] hover:underline underline-offset-2"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10 pointer-events-none" />
                    <PasswordInput
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      inputClassName="pl-10 h-12 bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-[#1e3a5f]/30 focus-visible:border-[#1e3a5f]"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="group relative w-full h-12 mt-2 bg-[#1e3a5f] hover:bg-[#152c4a] text-white text-base font-semibold shadow-lg shadow-[#1e3a5f]/25 transition-all disabled:opacity-80"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  <span className="inline-flex items-center gap-2">
                    {loading ? 'Signing in…' : 'Sign in'}
                    {!loading && (
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    )}
                  </span>
                </Button>

                {/* Divider */}
                <div className="relative pt-1">
                  <div className="absolute inset-0 flex items-center" aria-hidden>
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                    <span className="bg-white/90 px-3 text-slate-400 font-medium">
                      Or
                    </span>
                  </div>
                </div>

                <div className="text-center text-sm text-slate-500">
                  Don&apos;t have an account?{' '}
                  <Link
                    to="/register"
                    className="font-semibold text-[#1e3a5f] hover:text-[#152c4a] hover:underline underline-offset-2"
                    data-testid="register-link"
                  >
                    Create one
                  </Link>
                </div>
              </form>
            </div>
          </div>

          {/* Footer compliance strip */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              21 CFR Part 11 ready
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-slate-500" />
              End-to-end encrypted
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Audit-trail backed
            </span>
          </div>
        </div>
      </div>

      {/* ====================== RIGHT — Brand showcase ====================== */}
      <div
        className="relative hidden lg:flex lg:col-span-7 overflow-hidden"
        aria-hidden
      >
        {/* Layered navy gradient that matches landing CardSwap / MagicBento */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 80% at 0% 0%, rgba(56,189,248,0.18) 0%, transparent 55%), radial-gradient(100% 80% at 100% 100%, rgba(99,102,241,0.22) 0%, transparent 60%), linear-gradient(135deg, #0a1f3d 0%, #112b52 50%, #06122a 100%)',
          }}
        />
        {/* Dotted grid for depth (matches MagicBento) */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.85) 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
        {/* Glow accents */}
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[460px] h-[460px] rounded-full bg-fuchsia-500/15 blur-3xl" />
        {/* Top cyan hairline */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 opacity-80" />

        <div className="relative w-full flex flex-col justify-between p-12 xl:p-16 text-white">
          {/* Top — small platform pill */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.07] ring-1 ring-white/15 backdrop-blur text-[11px] uppercase tracking-[0.2em] font-semibold text-cyan-100">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
              Live · Clinical Operations
            </span>
            <span className="text-[11px] font-mono tracking-wider text-blue-100/60">
              clinicalflow.app
            </span>
          </div>

          {/* Middle — rotating headline */}
          <div className="max-w-xl">
            <div className="text-[11px] uppercase tracking-[0.28em] font-mono text-cyan-300/80 mb-5">
              The clinical research OS
            </div>
            <div key={heroIndex} className="animate-[fadeUp_0.55s_ease-out_both]">
              <h2 className="text-4xl xl:text-[44px] font-bold tracking-tight leading-[1.08]">
                {hero.title}
              </h2>
              <p className="mt-5 text-base xl:text-lg text-blue-100/80 leading-relaxed">
                {hero.subtitle}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-7">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setHeroIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === heroIndex ? 'w-10 bg-cyan-300' : 'w-5 bg-white/20 hover:bg-white/35'
                  }`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Bottom — KPI tiles + Activity feed */}
          <div className="grid grid-cols-12 gap-5">
            {/* KPI stack */}
            <div className="col-span-5 space-y-3">
              {kpis.map((k) => (
                <div
                  key={k.label}
                  className="rounded-2xl bg-white/[0.05] ring-1 ring-white/10 backdrop-blur-xl px-5 py-4 flex items-center justify-between"
                >
                  <div>
                    <div className={`text-3xl font-bold bg-gradient-to-r ${k.accent} bg-clip-text text-transparent`}>
                      {k.value}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-blue-100/70 mt-1">
                      {k.label}
                    </div>
                  </div>
                  <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${k.accent} opacity-90 shadow-lg shadow-black/40`} />
                </div>
              ))}
            </div>

            {/* Activity feed — mimics the in-app audit timeline */}
            <div className="col-span-7 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 backdrop-blur-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-blue-100/70">
                  Audit trail · Live
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-200 font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
                  Streaming
                </div>
              </div>
              <ul className="space-y-3">
                {activityFeed.map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-3 animate-[fadeUp_0.5s_ease-out_both]"
                      style={{ animationDelay: `${i * 110}ms` }}
                    >
                      <div className={`h-8 w-8 rounded-lg ring-1 ${a.ring} ${a.bg} ${a.accent} flex items-center justify-center shrink-0`}>
                        <Icon className="h-4 w-4" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white truncate">
                          {a.text}
                        </div>
                        <div className="text-[11px] text-blue-100/60 font-mono mt-0.5">
                          {a.meta}
                        </div>
                      </div>
                      <CheckCircle2 className="h-4 w-4 text-emerald-300/80 mt-1.5 shrink-0" />
                    </li>
                  );
                })}
              </ul>
              <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[11px] text-blue-100/60 font-mono">
                <span>QR-verified · immutable</span>
                <span>v2026.02</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Local keyframes for the rotating headline + feed reveal */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
